import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Youtube, MapPin, Phone, Mail } from "lucide-react";

const Footer = () => {
    const VITE_NEXTJS_URL = import.meta.env.VITE_NEXTJS_URL;

    useEffect(() => {
        // Initialize Trustpilot widget
        const trustpilotScript = document.createElement('script');
        trustpilotScript.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
        trustpilotScript.async = true;
        document.head.appendChild(trustpilotScript);

        trustpilotScript.onload = () => {
            if ((window as any).Trustpilot) {
                const widget = document.getElementById('trustpilot-widget');
                if (widget) {
                    (window as any).Trustpilot.loadFromElement(widget);
                }
            }
        };
    }, []);
    const sections = {
        solutions: [
            { name: "Astrologer", href: "/solutions/astrologer" },
            { name: "Real Estate", href: "/solutions/real-estate" },
            { name: "Healthcare", href: "/solutions/healthcare" },
            { name: "Education", href: "/solutions/education" },
            { name: "Logistics", href: "/solutions/logistics" },
            { name: "IT Companies", href: "/solutions/it-companies" },
            { name: "Marketing Agencies", href: "/solutions/marketing-agencies" },
            { name: "Hotels & Restaurants", href: "/solutions/hotels-restaurants" },
            { name: "Travel Agencies", href: "/solutions/travel" },
            { name: "Salons & Spas", href: "/solutions/salon" },
            { name: "Banking & Fintech", href: "/solutions/banking-fintech" },
        ],
        freeTools: [
            { name: "All Free Tools", href: "/free-tools" },
            { name: "WhatsApp Chat Button", href: "/free-tools/whatsapp-chat-button" },
            { name: "WhatsApp QR Code Generator", href: "/free-tools/whatsapp-qr-code-generator" },
            { name: "Free WhatsApp Link Generator", href: "/free-tools/free-whatsapp-link-generator" },
        ],
        quicklinks: [
            { name: "Home", href: "/" },
            { name: "Solutions", href: "/solutions" },
            { name: "AI Chatbot", href: "/ai-chatbot" },
            { name: "Pricing", href: "/pricing" },
            { name: "Blogs", href: "/blogs" },
            { name: "Contact Us", href: "/contactUs" },
            { name: "Documentation", href: "/docs", external: true },
            { name: "Affiliate Program", href: "/affiliate" },
            { name: "Cancellation & Refunds", href: "/cancellationAndRefunds" },
            { name: "Shipping", href: "/shipping" },
        ],
        partners: [
            { name: "All Partner Programs", href: "/" },
            { name: "Solutions Partner Program", href: "/" },
            { name: "Technology Partner Program", href: "/" },
            { name: "HubSpot for Startups", href: "/" },
        ]
    };

    return (
        <footer className="overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 text-white py-16 font-sans">
            <div className="container mx-auto px-6 max-w-7xl ">
                {/* Header Tagline */}
                <div className="text-center mb-12 pb-10 border-b border-gray-700/50">
                    <p className="text-lg md:text-xl text-gray-200 font-medium mb-3">
                        Create intelligent AI assistants for your website in minutes without coding.
                    </p>
                    <p className="text-sm text-gray-400">
                        TheQuickAssist is a product of{' '}
                        <a
                            href="https://prominno.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 hover:underline transition-colors font-medium"
                        >
                            Prominno Labs Pvt. Ltd.
                        </a>
                    </p>
                </div>

                {/* Main Links Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 pb-6">

                    <div>
                        <h4 className="font-bold text-sm mb-6 uppercase tracking-wider text-gray-100">Quick Links</h4>
                        <ul className="space-y-3 text-[13px] text-gray-300">
                            {sections.quicklinks.map((item) => (
                                <li key={item.name}>
                                    <a
                                        href={item.external ? item.href : `${VITE_NEXTJS_URL}${item.href}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative inline-flex items-center gap-1 text-gray-300 hover:text-white transition-all duration-300 ease-out"
                                    >
                                        <span className="relative">
                                            {item.name}
                                            <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300 ease-out"></span>
                                        </span>
                                        <svg
                                            className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out text-blue-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {/* Solutions Column 1 */}
                    <div>
                        <h4 className="font-bold text-sm mb-6 uppercase tracking-wider text-gray-100">Solutions</h4>
                        <ul className="space-y-3 text-[13px] text-gray-300">
                            {sections.solutions.map((item) => (
                                <li key={item.name}>
                                    <a
                                        href={`${VITE_NEXTJS_URL}${item.href}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative inline-flex items-center gap-1 text-gray-300 hover:text-white transition-all duration-300 ease-out"
                                    >
                                        <span className="relative">
                                            {item.name}
                                            <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300 ease-out"></span>
                                        </span>
                                        <svg
                                            className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out text-blue-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {/* Solutions Column 2 */}
                    {/* <div>
                        <h4 className="font-bold text-sm mb-6 uppercase tracking-wider text-gray-100">Solutions</h4>
                        <ul className="space-y-3 text-[13px] text-gray-300">
                            {sections.solutions.slice(5).map((item) => (
                                <li key={item.name}><Link href={item.href} className="hover:text-blue-400 hover:underline transition-all">{item.name}</Link></li>
                            ))}
                        </ul>
                    </div> */}
                    <div>
                        <h4 className="font-bold text-sm mb-6 uppercase tracking-wider text-gray-100">Free Tools</h4>
                        <ul className="space-y-3 text-[13px] text-gray-300">
                            {sections.freeTools.map((item) => (
                                <li key={item.name}>
                                    <a
                                        href={`${VITE_NEXTJS_URL}${item.href}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative inline-flex items-center gap-1 text-gray-300 hover:text-white transition-all duration-300 ease-out"
                                    >
                                        <span className="relative">
                                            {item.name}
                                            <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300 ease-out"></span>
                                        </span>
                                        <svg
                                            className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out text-blue-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>


                    <div>
                        <h4 className="font-bold text-sm mb-6 uppercase tracking-wider text-gray-100">Contact Us</h4>
                        <div className="space-y-4 text-[13px] text-gray-300">
                            {/* Address */}
                            <div className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-white mt-0.5 shrink-0" />
                                <a
                                    href="https://maps.app.goo.gl/pnKoM3fdqM8NJAzX7"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="leading-relaxed hover:text-white transition-colors"
                                >
                                    Office No. 704, City Avenue, Wakad, Pune, Maharashtra. 411057.
                                </a>
                            </div>

                            {/* Phone */}
                            <div className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-white shrink-0" />
                                <a
                                    href="tel:+919209764995"
                                    className="hover:text-white transition-colors"
                                >
                                    +91 92097 64995
                                </a>
                            </div>

                            {/* Email */}
                            <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-white shrink-0" />
                                <a
                                    href="mailto:support@thequickassist.co"
                                    className="hover:text-white transition-colors"
                                >
                                    support@thequickassist.co
                                </a>
                            </div>
                        </div>
                    </div>
                    {/* 3D Avatar Column - Integrated into the HubSpot style */}
                    <div className="flex flex-col items-center lg:justify-start justify-center mx-auto col-span-2 lg:col-span-1 w-full">
                         <div className="w-full h-40 relative">
                            <video
                                src="https://pub-0806a031e177428fb33e3291c1ee144e.r2.dev/footer_video.webm"
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-contain rounded-2xl"
                            />
                        </div>
                        <div className="mt-4">
                            <div
                                id="trustpilot-widget"
                                className="trustpilot-widget"
                                data-locale="en-US"
                                data-template-id="56278e9abfbbba0bdcd568bc"
                                data-businessunit-id="69119bdffb3fc94434b58bba"
                                data-style-height="52px"
                                data-style-width="100%"
                                data-token="d3e77872-50c6-482c-945e-df11adb42799"
                            >
                                <a
                                    href="https://www.trustpilot.com/review/thequickassist.co"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    Trustpilot
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Social Bar */}
                {/* Social Bar with Divider Lines */}
                <div className="flex items-center gap-6 mb-6">
                    <div className="flex-1 h-px bg-gray-600 hidden md:block"></div>
                    <div className="flex gap-6 mx-auto">
                        <a
                            href="https://www.instagram.com/thequickassist/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transform transition-transform hover:scale-110"
                            aria-label="Instagram"
                        >
                            <img src="/images/instagram.webp" alt="Instagram" width={32} height={32} className="w-8 h-8" />
                        </a>
                        <a
                            href="https://www.facebook.com/profile.php?id=61575158037374"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transform transition-transform hover:scale-110"
                            aria-label="Facebook"
                        >
                            <img src="/images/facebook.webp" alt="Facebook" width={32} height={32} className="w-8 h-8" />
                        </a>
                        <a
                            href="https://x.com/thequickassist"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transform transition-transform hover:scale-110"
                            aria-label="Twitter"
                        >
                            <img src="/images/tweeter.webp" alt="Twitter" width={32} height={32} className="w-8 h-8" />
                        </a>
                        <a
                            href="https://www.linkedin.com/company/thequickassist-ai/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transform transition-transform hover:scale-110"
                            aria-label="LinkedIn"
                        >
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_1308_16369)">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.22656 0H24.7734C26.6884 0.00552235 28.5233 0.772212 29.8773 2.13258C31.2314 3.49294 31.9945 5.3364 32 7.26024V24.7424C31.9938 26.6658 31.2304 28.5086 29.8764 29.8684C28.5224 31.2282 26.6879 31.9945 24.7734 32H7.22656C5.3121 31.9945 3.47758 31.2282 2.1236 29.8684C0.769619 28.5086 0.00618337 26.6658 0 24.7424V7.26024C0.00549674 5.3364 0.76863 3.49294 2.12268 2.13258C3.47674 0.772212 5.31165 0.00552235 7.22656 0Z" fill="#0A66C2" />
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12.8099 12.4038H16.8542V14.4969H16.9115C17.4766 13.4818 18.8516 12.4038 20.9063 12.4038C25.1745 12.4038 25.9635 15.0725 25.9635 18.5443V25.8857H21.7474V19.3606C21.7474 17.8667 21.7161 15.9437 19.5547 15.9437C17.3932 15.9437 17.0234 17.5684 17.0234 19.2533V25.8987H12.8099V12.4038ZM10.4167 8.31718C10.4167 8.75288 10.2881 9.1788 10.0471 9.54107C9.80619 9.90334 9.46374 10.1857 9.06307 10.3524C8.66241 10.5192 8.22153 10.5628 7.79618 10.4778C7.37084 10.3928 6.98014 10.183 6.67348 9.87489C6.36682 9.5668 6.15799 9.17428 6.07338 8.74695C5.98878 8.31963 6.0322 7.87669 6.19816 7.47416C6.36412 7.07163 6.64517 6.72758 7.00576 6.48552C7.36634 6.24346 7.79028 6.11426 8.22396 6.11426C8.80529 6.11495 9.36261 6.34727 9.77368 6.76024C10.1847 7.17322 10.416 7.73314 10.4167 8.31718ZM6.03646 12.4038H10.4167V25.8857H6.03646V12.4038Z" fill="white" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1308_16369">
                                        <rect width="32" height="32" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </a>
                        <a
                            href="https://www.youtube.com/@thequickassistcrm"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transform transition-transform hover:scale-110"
                            aria-label="YouTube"
                        >
                            <div className="w-8 h-8 bg-[#FF0000] rounded-lg flex items-center justify-center shadow-sm">
                                <Youtube className="w-5 h-5 text-white" />
                            </div>
                        </a>
                    </div>

                    <div className="flex-1 h-px bg-gray-600 hidden md:block"></div>
                </div>

                {/* Logo & Copyright */}
                <div className="flex flex-col items-center gap-4 text-center">
                    <Link to="/">
                        <img
                            width={180}
                            height={100}
                            src="/images/thequickassist-logo-dark.webp"
                            alt="theQuickAssist.co"
                            className="h-6 md:h-[2.1rem] w-auto transition-all"
                        />
                    </Link>

                    <p className="text-xs text-gray-400 font-medium">
                        © {new Date().getFullYear()} TheQuickAssist.co. All rights reserved.
                    </p>

                    {/* Legal Links */}
                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-[12px] font-semibold text-gray-300">
                        <Link
                            to={`${VITE_NEXTJS_URL}/privacyPolicy`}
                            target="_blank"
                            className="group relative text-gray-300 hover:text-white transition-all duration-300 ease-out"
                        >
                            <span className="relative">
                                Privacy Policy
                                <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300 ease-out"></span>
                            </span>
                        </Link>
                        <Link
                            to={`${VITE_NEXTJS_URL}/termsAndCondition`}
                            target="_blank"
                            className="group relative text-gray-300 hover:text-white transition-all duration-300 ease-out"
                        >
                            <span className="relative">
                                Terms of Service
                                <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300 ease-out"></span>
                            </span>
                        </Link>
                        <Link
                            to={`${VITE_NEXTJS_URL}/cookie-policy`}
                            target="_blank"
                            className="group relative text-gray-300 hover:text-white transition-all duration-300 ease-out"
                        >
                            <span className="relative">
                                Cookie Policy
                                <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300 ease-out"></span>
                            </span>
                        </Link>
                    </div>

                </div>
            </div>
        </footer>
    );
};

export default Footer;
